import { Translation } from '../types';

export const en: Translation = {
  common: {
    loading: 'Loading...',
    error: 'Error',
    cancel: 'Cancel',
    save: 'Save',
    close: 'Close',
    search: 'Search',
    reload: 'Reload',
    logout: 'Logout',
    dashboard: 'Dashboard',
    products: 'Products',
    settings: 'Settings',
    language: 'Language',
    businessHours: 'Business Hours',
    back: 'Back',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    success: 'Success',
    selected: 'Selected',
    confirm: 'Confirm',
    required: 'Required',
    requiredMarker: '*',
    previous: 'Previous',
    next: 'Next',
    allRightsReserved: 'All rights reserved.',
  },
  auth: {
    login: 'Login',
    email: 'Email Address',
    password: 'Password',
    loginButton: 'Login',
    loggingIn: 'Logging in...',
    loginSuccess: 'Login successful',
    loginError: 'Email or password is incorrect',
    invalidEmail: 'Please enter a valid email address',
    requiredFields: 'Please enter email and password',
    demoAccount: 'Demo Account:',
    loginDescription: 'Enter your account information to log in',
    welcomeMessage: 'Welcome to the business ordering system',
    // Registration
    register: 'Register',
    createAccount: 'Create Account',
    confirmPassword: 'Confirm Password',
    passwordRequirements: 'Password must be at least 8 characters with uppercase, lowercase, number, and special character',
    passwordsDoNotMatch: 'Passwords do not match',
    registrationSuccess: 'Registration successful',
    registrationDescription: 'Register for a business account',
    alreadyHaveAccount: 'Already have an account?',
    signIn: 'Sign in',
    dontHaveAccount: "Don't have an account?",
    creatingAccount: 'Creating account...',
    registrationNote: 'Note:',
    registrationNotePoints: {
      erpLinking: 'If you have an existing business account, your email will be automatically linked',
      companyEmail: 'Please use your company email address for registration',
      contactSupport: 'Contact support if you need assistance with registration'
    },
    b2bUsers: 'Business Account Users:',
    registeredEmail: 'Registered email address',
    registeredPassword: 'Password set during registration',
    b2bCustomerRequired: 'This email address is not registered as a business customer. Please contact sales to establish a business relationship first.',
    b2bVerificationError: 'Unable to verify business customer status. Please contact support.',
    // Password Reset
    passwordReset: 'Password Reset',
    passwordResetDescription: 'Enter your email address to get a temporary password',
    getTemporaryPassword: 'Get Temporary Password',
    processing: 'Processing...',
    passwordResetComplete: 'Password Reset Complete',
    temporaryPasswordGenerated: 'Temporary password has been generated',
    temporaryPassword: 'Temporary Password',
    backToLogin: 'Back to Login',
    securityNotice: 'Security Notice:',
    securityNotePoints: {
      keepSecret: 'Do not share the temporary password with others',
      changeAfterLogin: 'Be sure to change your password after logging in',
      expires24Hours: 'The temporary password expires after 24 hours'
    },
    resetNote: 'Note:',
    resetNotePoints: {
      registeredEmail: 'Enter your registered email address',
      passwordDisplayed: 'The temporary password will be displayed on screen',
      changeRequired: 'Password change is required after login'
    },
    copied: 'Copied',
    copySuccessMessage: 'Temporary password copied to clipboard.',
    forgotPassword: 'Forgot password?',
    // Password Reset Email Request
    sendResetLink: 'Send Reset Link',
    sendingEmail: 'Sending email...',
    emailSent: 'Email Sent',
    emailSentMessage: 'If this email address exists in our system, you will receive a password reset link shortly.',
    emailNotSent: 'Email not sent',
    emailSentInfo: 'Check your email inbox and spam folder',
    checkEmail: 'Check Email',
    emailInstructions: 'Password reset instructions have been sent',
    nextSteps: 'Next Steps:',
    nextStepsList: {
      checkInbox: 'Check your email inbox and spam folder',
      clickLink: 'Click the reset link in the email',
      linkExpires: 'The link expires in 24 hours',
      contactSupport: 'Contact support if you don\'t receive the email',
      rateLimitInfo: 'Limited to 3 requests per hour'
    },
    // Password Reset Completion
    resetLinkSent: 'Reset Link Sent',
    resetSuccess: 'Password Successfully Reset',
    passwordUpdated: 'Password Updated',
    passwordUpdateMessage: 'Your password has been successfully updated. You can now log in with your new password.',
    loginWithNewPassword: 'Login with New Password',
    // Reset Token Page
    resetToken: 'Reset Password',
    invalidToken: 'Invalid reset token',
    expiredToken: 'Reset token has expired',
    tokenValid: 'Reset token is valid',
    newPassword: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    updatePassword: 'Update Password',
    updatingPassword: 'Updating password...',
    passwordMismatch: 'Passwords do not match',
    resetInstructions: 'Enter your new password to complete the reset',
  },
  accountInfo: {
    title: 'Account Information',
    loading: 'Loading account information...',
    error: 'Error',
    errorMessage: 'Failed to retrieve account information',
    back: 'Back',
    basicInfo: 'Basic Information',
    name: 'Name',
    email: 'Email Address',
    contactId: 'Knitido Customer ID',
    contactNumber: 'Contact Number',
    noInfo: 'No information',
    companyInfo: 'Company Information',
    company: 'Company Name',
    position: 'Position',
    vatId: 'VAT ID',
    billingAddress: 'Billing Address',
    deliveryAddress: 'Delivery Address',
    addressInfo: 'Address Information',
    billingDescription: 'Address for invoices and contract documents',
    deliveryDescription: 'Standard delivery address for product shipments',
    sameAsBilling: '※ Same as billing address',
    sameAsDelivery: '※ Same as delivery address',
    companyName: 'Company Name',
    contactPerson: 'Contact Person',
    address: 'Address',
    phoneNumber: 'Phone Number',
    billingUsage: 'Used as the destination for invoices, contracts, and other important documents',
    deliveryUsage: 'Used as the standard delivery address for ordered products',
    alsoUsedForDelivery: 'Also used as the delivery destination',
    accountType: 'Account Type',
    addressId: 'Address ID',
    // Address editing functionality
    edit: 'Edit',
    editDeliveryAddress: 'Edit Delivery Address',
    editDeliveryDescription: 'Update your delivery address information',
    newDeliveryAddress: 'Add New Delivery Address',
    newDeliveryDescription: 'Enter the details for your new delivery address',
    noDeliveryAddress: 'No delivery address configured',
    success: 'Success',
    addressUpdated: 'Delivery address updated successfully',
    updateError: 'Failed to update delivery address',
    requiredFields: 'Please fill in all required fields',
    firstName: 'First Name',
    lastName: 'Last Name',
    streetAddress: 'Street Address',
    streetAddress2: 'Street Address 2',
    city: 'City',
    country: 'Country',
    selectCountry: 'Select country...',
    searchCountry: 'Search country...',
    noCountryFound: 'No country found.',
    postalCode: 'Postal Code',
    companyNamePlaceholder: 'Enter company name',
    firstNamePlaceholder: 'First name',
    lastNamePlaceholder: 'Last name',
    streetAddressPlaceholder: 'Enter street address',
    streetAddress2Placeholder: 'Apartment, suite, etc.',
    cityPlaceholder: 'Enter city',
    postalCodePlaceholder: 'Enter postal code',
    billingNotEditable: 'Billing address cannot be edited.',
    billingContactSupport: 'Contact support for changes.',
    // Multiple delivery addresses
    deliveryAddressCount: '{count} delivery addresses',
    noDeliveryAddresses: 'No delivery addresses registered',
    defaultAddress: 'Default',
    selectDeliveryAddress: 'Select delivery address',
    addNewAddress: 'Add new address',
    editAddress: 'Edit address',
    deleteAddress: 'Delete address',
    confirmDelete: 'Confirm deletion',
    confirmDeleteMessage: 'Are you sure you want to delete this address? This action cannot be undone.',
    addressAddedSuccess: 'Delivery address added',
    addressDeletedSuccess: 'Delivery address deleted',
    deleteError: 'Failed to delete delivery address',
    saving: 'Saving...',
    cancel: 'Cancel',
    saveChanges: 'Save changes',
    selectedDeliveryAddress: 'Selected delivery address',
  },
  dashboard: {
    title: 'Business Partner',
    subtitle: 'Product Ordering System',
    welcome: 'Welcome,',
    welcomeMessage: 'Welcome to the Business Partner ordering system. You can order products here.',
    productCatalog: 'Product Catalog',
    productCatalogDesc: 'Browse and order available products',
    viewProducts: 'View Products',
    orderHistory: 'Order History',
    orderHistoryDesc: 'View past orders (coming soon)',
    accountSettings: 'Account Settings',
    accountSettingsDesc: 'Manage account information (coming soon)',
    comingSoon: 'Coming Soon',
    infoTitle: 'About Usage',
    orderingInfo: 'Product Ordering',
    orderingDesc: 'Select products from the catalog and specify size and color. Real-time inventory information is available.',
    supportInfo: 'Support',
    supportDesc: 'If you have any questions, please contact us.',
    viewOrderHistory: 'View Order History',
    viewOrders: 'View Orders',
    accountInfo: 'Account Information',
    accountInfoDesc: 'View your account details',
    viewDetails: 'View Details',
    supportEmail: 'b2b-partner@knitido.de',
    supportPhone: '+49(0)30 6640 9300',
    businessHours: 'Monday-Friday 9:00 AM - 3:00 PM CET',
    bulkImportDesc: 'Import orders from Excel/CSV files',
    importOrders: 'Import Orders',
  },
  products: {
    title: 'Product List',
    subtitle: 'Business Partner',
    searchPlaceholder: 'Search by product name or SKU...',
    productId: 'Product ID',
    addToCart: 'Add to Cart',
    selectVariation: 'Select Variation',
    price: 'Price',
    priceNetNotice: 'All prices are net prices',
    stock: 'Stock',
    color: 'Color',
    newProducts: 'New Products',
    newProductBadge: 'NEW',
    size: 'Size',
    quantity: 'Quantity',
    total: 'Total',
    cart: 'Cart',
    checkout: 'Checkout',
    csvMode: 'CSV Fast Mode',
    csvInfo: 'Product data retrieved from CSV data',
    csvUploadDate: 'CSV Upload Date:',
    stockNote: '※ Stock information is retrieved when selecting product variations',
    noProducts: 'No products found',
    noProductsDesc: 'Change search criteria or upload a CSV file for faster performance.',
    retry: 'Retry',
    pagination: 'items',
    variations: 'variations',
    stockFetchError: 'Stock fetch error',
    stockFetchFailed: 'Stock fetch failed',
    stockFetchFailedDesc: 'Could not retrieve stock information. CSV stock will be displayed.',
    stockFetched: 'Stock information retrieved',
    totalStock: 'Total stock',
    noStockAll: 'All variations have zero stock',
    noStockData: 'No stock data returned',
    stockFetchErrorDesc: 'Error occurred while fetching stock information',
    selectionError: 'Selection error',
    enterQuantity: 'Please enter quantity',
    addedToCart: 'Added to cart',
    typesAdded: ' product types added',
    addToCartError: 'Error adding to cart',
    addToCartErrorDesc: 'Please try again',
    loadingStock: 'Loading stock information',
    csvDataInfo: 'Displaying CSV data information (not actual stock levels)',
    invalidDataFormat: 'Product data format is invalid',
    loadFailed: 'Failed to load product data',
    csvUploadComplete: 'CSV upload completed',
    csvLoadedDesc: ' product data loaded. Stock information will be retrieved when selecting products.',
    searchTitle: 'Product Search',
    loadingProducts: 'Loading product data',
    noProductsCsv: 'No products found in CSV data. Please change search criteria.',
    paginationDisplay: ' displayed',
    previous: 'Previous',
    next: 'Next',
    selectionSummary: 'Selection Summary',
    selectedQuantity: 'Selected Quantity',
    items: ' items',
    viewDetails: 'View Details',
    productImage: 'Product Image',
    correspondingVariations: 'Corresponding Variations',
    clickToEnlarge: 'Click to enlarge',
    productIdLabel: 'Product ID',
    colorLabel: 'Color',
    filterByCategory: 'Filter by Category',
    allProducts: 'All Products',
    applyFilters: 'Apply Filters',
    clearFilters: 'Clear Filters',
    filters: 'Filters',
    clearAll: 'Clear all',
  },
  categories: {
    category_21: 'Toe Socks',
    category_23: 'Uncategorized',
    category_24: 'Body Wear',
    category_29: 'B2B Products',
  },
  productDetail: {
    title: 'Product Details',
    backToProducts: 'Back to Products',
    productNotFound: 'Product Not Found',
    productNotFoundDesc: 'The specified product does not exist or has been deleted.',
    loading: 'Loading product...',
    imageLoadError: 'Failed to load images',
    noImages: 'No images available',
    productDescription: 'Product Description',
    colors: 'Colors',
    sizes: 'Sizes',
    colorCount: 'colors',
    sizeCount: 'sizes',
    variationCount: 'variations',
    selectVariation: 'Select Variations',
    descriptionLanguageNote: '※ Product descriptions are available in English or German only',
  },
  colors: {
    schwarz: 'Black',
    weiß: 'White',
    grau: 'Gray',
    rot: 'Red',
    blau: 'Blue',
    grün: 'Green',
    gelb: 'Yellow',
    rosa: 'Pink',
    lila: 'Purple',
    braun: 'Brown',
    orange: 'Orange',
    beige: 'Beige',
    navy: 'Navy',
    dunkelblau: 'Dark Blue',
    hellblau: 'Light Blue',
    dunkelgrau: 'Dark Gray',
    hellgrau: 'Light Gray',
    bordeaux: 'Bordeaux',
    türkis: 'Turquoise',
    khaki: 'Khaki',
    mint: 'Mint',
    violett: 'Violet',
    anthrazit: 'Anthracite',
    blaugrau: 'Blue Gray',
    burgunderrot: 'Burgundy',
    elfenbein: 'Ivory',
    karminrot: 'Carmine',
    neongrün: 'Neon Green',
    gelbgrün: 'Yellow Green',
    pistazie: 'Pistachio',
    // English color names (used in Product ID 237 etc.)
    blue: 'Blue',
    ivory: 'Ivory',
    green: 'Green',
    gold: 'Gold',
    red: 'Red',
  },
  checkout: {
    title: 'Checkout',
    cartReview: 'Order Review',
    orderSummary: 'Order Summary',
    shippingInfo: 'Shipping Information',
    billingInfo: 'Billing Information',
    orderNotes: 'Order Notes',
    orderNotesPlaceholder: 'Special requests or notes...',
    contactPerson: 'Contact Person',
    phoneNumber: 'Phone Number',
    email: 'Email Address',
    company: 'Company',
    address: 'Address',
    city: 'City',
    postalCode: 'Postal Code',
    country: 'Country',
    deliveryDate: 'Delivery Date',
    urgentDelivery: 'Express Delivery (3-5 business days)',
    standardDelivery: 'Standard Delivery (7-10 business days)',
    placeOrder: 'Place Order',
    backToCart: 'Back to Product Selection',
    requiredField: 'Required field',
    orderTotal: 'Order Total',
    subtotal: 'Subtotal',
    priceNetNotice: 'All prices are net prices',
    stockReservationNotice: 'Stock reservation will be made after order confirmation.',
    stockCheckError: 'Stock Check Error',
    stockCheckErrorDesc: 'Error occurred during stock check. Please try again.',
    shipping: 'Shipping',
    shippingCost: 'Shipping Cost',
    tax: 'Tax',
    orderSuccess: 'Order Successful',
    orderSuccessMessage: 'Your order has been successfully submitted. Order number: {orderNumber}',
    orderNumber: 'Order Number',
    thankYou: 'Thank you for your order!',
    orderConfirmation: 'Your order has been successfully submitted.',
    orderProcessing: 'After review by our team, we will send you an order confirmation via email.',
    orderProcessingMessage: 'Your order has been successfully submitted. After review by our team, we will send you an order confirmation via email.',
    continueShopping: 'Continue Shopping',
    emptyCart: 'Your cart is empty',
    emptyCartMessage: 'Please add products to your cart to continue',
    stockWarning: 'Stock Warning',
    stockWarningDesc: 'Some items have insufficient stock. Please adjust quantities.',
    stockError: 'Stock Error',
    stockErrorDesc: 'There are items with insufficient stock. Please adjust quantities.',
    refreshStock: 'Refresh Stock',
    stockUpdated: 'Stock Updated',
    stockUpdatedDesc: 'Stock has been updated - {changed} items changed',
    stockCheckDesc: 'Comparison results with inventory are displayed',
    stockOk: 'Stock OK',
    stockShortage: 'Stock Shortage',
    requestedQty: 'Requested Qty',
    currentStock: 'Current Stock',
    stockShortageMsg: 'Insufficient stock. Please adjust the quantity.',
    stockIssueWarning: 'Order cannot be confirmed due to stock issues.',
    stockDataMissing: 'Stock data is being retrieved. Please wait a moment.',
    outOfStock: 'Out of stock.',
    maxAvailable: 'Maximum',
    autoRestored: 'Cart restored',
    autoRestoredDesc: 'Items from your last session have been restored',
    maxQuantityReached: 'Maximum stock quantity reached.',
    // ERP Contact Search
    contactSearch: 'Customer Contact Search',
    contactSearchDesc: 'Enter the orderer\'s email address to retrieve contact information',
    emailAddress: 'Email Address',
    searchButton: 'Search',
    searching: 'Searching',
    contactFound: 'Contact Found',
    contactNotFound: 'Contact Not Found',
    contactId: 'Knitido Customer ID',
    companyName: 'Company',
    contactPersonLabel: 'Contact Person',
    addressesFound: 'Addresses Found',
    addressType: 'Type',
    addressBilling: 'Billing Address',
    addressDelivery: 'Delivery Address',
    vatSourceCompany: 'Company Info',
    vatSourceBilling: 'Billing',
    vatSourceDelivery: 'Delivery',
    addressOther: 'Other',
    addressDetails: 'Address',
    loadingAddresses: 'Loading address information...',
    addressRetrieved: 'Address information retrieved',
    addressNotFound: 'Address information not found',
    addressNotFoundDesc: 'No address information registered. Please enter manually.',
    addressError: 'Address Retrieval Error',
    addressErrorDesc: 'Failed to retrieve address information.',
    contactNotConfirmed: 'Customer Contact Not Confirmed',
    contactNotConfirmedDesc: 'Please enter an email address and search for the contact.',
    searchContact: 'Search Contact',
    inputError: 'Input Error',
    inputErrorDesc: 'Shipping information is incomplete. Please check.',
    processing: 'Processing...',
    erpContactSearch: 'Customer Contact Search',
    contactSearchError: 'Email address does not correspond to any registered contact.',
    // Product Images Toggle
    showProductImages: 'Show Product Images',
    showProductImagesDesc: 'Display product images for better overview (may increase loading time)',
    // System Registration Info
    systemRegistrationInfo: 'Your Customer Information',
    editShippingAddress: 'Edit Delivery Address',
    systemRegistrationInfoDesc: 'Your registered business information. Please contact your representative if you need to update billing address details.',
    // Order Summary
    regularPrice: 'Regular Wholesale Price',
    offeredPrice: 'Offered Price',
    discount: 'Discount',
    b2bCustomer: 'B2B Customer',
    pattern: 'Pattern',
    specialPriceApplied: 'Special price applied',
    customerClassification: 'Customer Classification',
    paymentCondition: 'Payment Terms',
    invoice: 'Invoice (NET 30)',
    prepayment: 'Prepayment',
    paymentMethod: 'Payment Method',
    invoicePayment: 'Invoice Payment',
    estimatedSubtotal: 'Estimated Subtotal',
    estimatedTotal: 'Estimated Total',
    orderSummaryDisclaimer: 'The prices shown here are estimates. Once we receive your order, we will review it and send you a binding order confirmation with final pricing, shipping costs, and taxes via email.',
    searchError: 'Search Error',
    searchErrorDesc: 'An error occurred during contact search.',
    confirmDeleteItem: 'Remove Item',
    itemDeleted: 'Item removed',
    confirmDeleteItemDesc: 'Are you sure you want to remove the following item from your cart?',
    deleteConfirm: 'Remove',
    contactNotes: 'Contact Notes',
    contactNotesDesc: 'Do you have any questions or special requests? Please let us know (optional)',
    contactNotesLabel: 'Message',
    contactNotesPlaceholder: 'e.g.: Product questions, special requirements, etc.',
    characters: 'characters',
    contactIdLabel: 'Knitido Customer ID',
    companyLabel: 'Company',
    personLabel: 'Contact Person',
    emailLabel: 'Email',
    addressTypeLabel: 'Type',
    destinationLabel: 'Destination',
    // Shipping Form
    shippingTitle: 'Shipping Information',
    shippingDesc: 'Enter the delivery address for the products',
    companyRequired: 'Company *',
    contactPersonRequired: 'Contact Person *',
    emailRequired: 'Email Address *',
    phoneRequired: 'Phone Number *',
    addressRequired: 'Address 1 *',
    address2: 'Address 2 (Building/Room)',
    postalCodeRequired: 'Postal Code *',
    cityRequired: 'City *',
    countryRequired: 'Country *',
    selectCountry: 'Select Country',
    shippingMethodRequired: 'Shipping Method *',
    selectShippingMethod: 'Select Shipping Method',
    urgentDeliveryOption: 'Express Delivery (additional charges)',
    deliveryNotes: 'Delivery Notes',
    deliveryNotesPlaceholder: 'Enter delivery time preferences or other requests',
    standardShipping: 'Standard Shipping (3-5 business days)',
    expressShipping: 'Express Shipping (1-2 business days)',
    overnightShipping: 'Overnight Shipping',
    germany: 'Germany',
    austria: 'Austria',
    switzerland: 'Switzerland',
    japan: 'Japan',
    shippingAddressLabel: 'Delivery Address',
    changeDeliveryAddress: 'Change Delivery Address',
    selectDeliveryAddress: 'Select Delivery Address',
    // Shipping Cost Details
    freeShipping: 'Free Shipping',
    shippingError: 'Error',
    shippingZone: 'Shipping Zone',
    remainingForFreeShipping: '{amount} more for free shipping',
    moreForFreeShipping: 'more for free shipping',
    freeShippingProgress: 'Free shipping at',
    andAboveFreeShipping: 'and above for free shipping',
    freeShippingAchieved: 'Free shipping applied',
    threshold: 'and above',
    nextShippingTier: 'Next shipping discount',
    moreForDiscount: 'more for',
    shippingSavings: 'shipping savings',
    orderAmountThreshold: 'and above for',
    specialRegionNotice: 'Special shipping region applied',
    defaultShippingApplied: 'Standard shipping rates applied',
    requestedQuantity: 'Requested Quantity',
    availableQuantity: 'Available Quantity',
    orderSubmitError: 'Order Submission Error',
    orderSubmitErrorDesc: 'Failed to submit order',
    upload: {
      title: 'Import Excel/CSV Order',
      description: 'Upload an Excel or CSV file with your order data',
      dragDropOrClick: 'Drag & drop file here or click to select',
      dropHere: 'Drop file here',
      supportedFormats: 'Supported formats: Excel (.xlsx, .xls) and CSV (.csv)',
      maxSize: 'Maximum file size: {size} MB',
      processFile: 'Process File',
      processing: 'Processing file...',
      ready: 'File ready for preview',
      readyDescription: 'Your file has been successfully processed and is ready for review.',
      preview: 'Show Preview',
      errors: {
        fileTooLarge: 'File is too large. Maximum size: {size} MB',
        invalidFileType: 'Invalid file type. Please upload an Excel or CSV file',
        uploadFailed: 'Error uploading file',
      },
    },
    // Loading messages
    loadingPage: 'Loading page...',
    loadingStock: 'Checking product inventory...',
    loadingAccount: 'Loading account information...',
    loadingError: 'An error occurred...',
    loadingData: 'Loading data',
    // Toast messages
    addressRetrievedTitle: 'Address information retrieved',
    addressSetAsDelivery: ' address set as delivery destination.',
    contactFoundDesc: ' was recognized.'
  },
  orderHistory: {
    title: 'Order History',
    searchPlaceholder: 'Search by order number or SKU...',
    noOrders: 'No orders found',
    noOrdersFound: 'No matching orders found',
    startShopping: 'View Products',
    orderItems: 'Order Items',
    viewDetails: 'Details',
    reorder: 'Reorder',
    orderDate: 'Order Date',
    orderStatus: 'Status',
    orderTotal: 'Order Total',
    // Status translations
    statusPending: 'Payment Pending',
    statusProcessing: 'Processing',
    statusConfirmed: 'Confirmed',
    statusShipped: 'Shipped',
    statusDelivered: 'Delivered',
    statusCancelled: 'Cancelled',
    statusReturned: 'Returned',
    statusRefunded: 'Refunded',
    statusWarranty: 'Warranty',
    statusExchanged: 'Exchanged',
    statusRepair: 'Repair',
    statusUnknown: 'Unknown',
    statusIncomplete: 'Incomplete',
    statusWaitingPayment: 'Waiting for Payment',
    statusInPreparation: 'In Preparation',
    statusClearedShipping: 'Cleared for Shipping',
    statusBeingShipped: 'Being Shipped',
    statusOutgoingBooked: 'Outgoing Booked',
    statusReturn: 'Return',
    // Additional UI elements
    itemCount: 'items',
    itemCountSingular: 'item',
    refreshButton: 'Refresh',
    errorFetchingOrders: 'Failed to fetch order history',
    // Payment status features
    paymentStatus: 'Payment Status',
    markAsPaid: 'Mark as Paid',
    unmarkAsPaid: 'Unmark as Paid',
    paidOn: 'Paid on',
    paymentMarked: 'Paid',
    notPaid: 'Not Paid',
    paymentMarkedSuccess: 'Successfully marked as paid',
    paymentUnmarkedSuccess: 'Payment mark removed',
    paymentUpdateError: 'Failed to update payment status',
    // Payment date dialog
    paymentDateTitle: 'Enter Payment Information',
    paymentDateDescription: 'Enter payment information for order {orderNumber}',
    paymentDateLabel: 'Payment Date',
    selectDate: 'Select date',
    paymentDateHelp: 'Select the date when the bank transfer was made',
    paymentMethodLabel: 'Payment Method',
    bankTransfer: 'Bank Transfer',
    invoice: 'Invoice',
    otherPayment: 'Other',
    paymentReferenceLabel: 'Transfer Name / Reference Number',
    paymentReferencePlaceholder: 'e.g., John Doe, Transfer #12345',
    paymentReferenceHelp: 'Enter information that helps match with bank statements',
    notesLabel: 'Notes',
    notesPlaceholder: 'Enter any additional payment information',
    // Filter options
    filterByPaymentStatus: 'Filter by Payment Status',
    allOrders: 'All Orders',
    paidOrders: 'Paid',
    unpaidOrders: 'Unpaid',
    // Sorting options
    sortBy: 'Sort by',
    sortByDate: 'Order Date',
    sortByPaymentDate: 'Payment Date',
    sortByAmount: 'Amount',
    sortByStatus: 'Status',
  },
  orderDetails: {
    title: 'Order Details',
    notFound: 'Order not found',
    backToHistory: 'Back to Order History',
    orderDate: 'Order Date',
    orderItems: 'Order Items',
    deliveryStatus: 'Delivery Status',
    estimatedDelivery: 'Estimated Delivery',
    deliveredOn: 'Delivered on',
    downloadInvoice: 'Download Invoice',
    statusDelivered: 'Delivered',
    statusShipped: 'Shipped',
    statusConfirmed: 'Confirmed',
    statusCancelled: 'Cancelled',
    errorFetchingDetails: 'Failed to fetch order details',
  },
  company: {
    name: 'Business Partner',
    tagline: 'Business Partner System',
    copyright: '© 2025 Business Partner System. All rights reserved.',
  },
  footer: {
    phone: 'Phone',
    email: 'Email',
    countries: {
      DE: 'Germany',
      AT: 'Austria',
      CH: 'Switzerland',
      FR: 'France',
      IT: 'Italy',
      ES: 'Spain',
      NL: 'Netherlands',
      BE: 'Belgium',
      PL: 'Poland',
      SE: 'Sweden',
      NO: 'Norway',
      FI: 'Finland',
      DK: 'Denmark',
      LT: 'Lithuania',
      LV: 'Latvia',
      EE: 'Estonia',
    },
  },
  cart: {
    newCart: 'New Cart',
    currentCart: 'Current Cart',
    modified: 'modified',
    lastSaved: 'Last saved',
    unsavedChanges: 'unsaved changes',
    saveCart: 'Save Cart',
    loadCart: 'Load Cart',
    deleteCart: 'Delete Cart',
    startNewCart: 'Start New Cart',
    savedCarts: 'Saved Carts',
    cartSaved: 'Cart saved',
    cartDeleted: 'Cart deleted',
    confirmDeleteCart: 'Delete cart?\n\nThis action cannot be undone.',
    confirmSwitchCart: 'The current cart has unsaved changes.\n\nLoading another cart will cause the current changes to be lost.\n\nContinue?',
    confirmSwitchCartTitle: 'Switch Cart',
    confirmNewCart: 'The current cart has unsaved changes.\n\nStarting a new cart will cause the current changes to be lost.\n\nContinue?',
    confirmNewCartTitle: 'Start New Cart',
    confirmClearCart: 'Clear cart?\n\n{count} items will be removed.\n\nThis action cannot be undone.',
    confirmClearCartTitle: 'Clear Cart',
    autoSaved: 'Auto-saved',
    itemsWillBeRemoved: 'items will be removed',
    changesWillBeLost: 'changes will be lost',
    continueQuestion: 'Continue?',
    cartStatus: 'Cart Status',
    cartName: 'Cart Name',
    cartNamePlaceholder: 'e.g.: End of month order',
    cartNameRequired: 'Please enter a cart name',
    saveCartDescription: 'Give the current cart a name and save it. You can load it later.',
    cancelCart: 'Cancel Cart',
    proceedToCheckout: 'Proceed to Checkout',
    emptyCart: 'Cart is empty',
    erpDown: 'The order system is down. Please try again later.',
    erpDegraded: 'The system is currently unstable. Orders may fail.',
    erpDownSaveHint: 'Please save your cart and place your order once the system is restored.',
    erpSaveCart: 'Save cart',
  },
  nav: {
    dashboard: 'Dashboard',
    products: 'Products',
    orderHistory: 'Order History',
    account: 'Account Info',
    bulkImport: 'Bulk Import',
  },
  settings: {
    selectLanguage: 'Select a Language',
    languagePreferenceSaved: 'Your preference will be saved to your account for future visits.',
  },
  reorder: {
    allItemsAdded: '{count} items added to cart',
    partialSuccess: '{added} items added. {failed} items could not be added',
    allItemsFailed: 'Failed to add items to cart',
    error: 'An error occurred during reorder',
    itemDiscontinued: '{name} ({color}/{size}) has been discontinued',
    itemOutOfStock: '{name} ({color}/{size}) is out of stock',
    partialStock: '{name} ({color}/{size}) only {available} available',
    confirmReorder: 'Confirm Reorder',
    confirmReorderMessage: 'Add items from this order to your cart?',
    addingToCart: 'Adding to cart...',
    checkingStock: 'Checking stock...',
    checkoutTitle: 'Items Added to Cart',
    checkoutDescription: '{count} items have been added to your cart. Would you like to proceed to checkout?',
  },
  bulkImport: {
    instructions: 'How to Import Orders',
    step1: 'Download the order template file',
    step2: 'Fill in your order data with SKU and quantity',
    step3: 'Save as Excel (.xlsx) or CSV file',
    step4: 'Upload the file using the form below',
    step5: 'Review and confirm your order',
    downloadTemplate: 'Download Template',
    downloadExcelTemplate: 'Download Excel Template',
    downloadCsvTemplate: 'Download CSV Template',
    formatGuide: 'File Format Guide',
    requiredColumns: 'Required Columns',
    skuDescription: 'Product SKU code',
    quantityDescription: 'Order quantity (positive integer)',
    optionalColumns: 'Optional Columns',
    notesDescription: 'Internal notes (e.g. PO number, reference) - for your records only',
    formatNote: 'The first row should contain column headers. Data starts from the second row.',
    parseSuccess: 'Successfully parsed {count} items from the file',
    parseError: 'Parse Error',
    validationError: 'Validation Errors',
    validationErrorGuidance: 'Please click the "{showPreviewButton}" button to review the details.',
    warnings: 'Warnings',
    itemsParsed: 'Successfully parsed {count} items:',
    andMore: '...and {count} more items',
    validationSummary: 'Validation Summary',
    duplicateSKUs: 'Duplicate SKUs found',
    skuFormat: 'SKU must contain only letters, numbers, and hyphens',
    quantityRange: 'Quantity must be between 1 and 9999',
    maxRows: 'Maximum 1000 rows allowed',
    rowsSkipped: '{count} rows were skipped due to errors',
    previewTitle: 'Order Preview',
    previewDescription: 'Review and edit your order data before proceeding',
    proceedToCart: 'Add to Cart',
    // File parsing messages
    fileEmpty: 'File is empty or contains no data',
    fileTooManyRows: 'File contains too many rows ({current}). Maximum allowed: {max}',
    invalidRowFormat: 'Invalid row format',
    missingColumns: 'Missing required columns (SKU and Quantity)',
    missingSku: 'Missing SKU',
    invalidSkuFormat: 'Invalid SKU format: "{sku}" (must match pattern: {pattern})',
    skuTooLong: 'SKU too long: "{sku}" (maximum 50 characters)',
    invalidQuantity: 'Invalid quantity: "{quantity}" is not a number',
    quantityTooLow: 'Quantity too low: {quantity} (minimum: {min})',
    quantityTooHigh: 'Quantity too high: {quantity} (maximum: {max})',
    notesRequired: 'Notes field is required',
    noValidData: 'No valid data found. Please check your file format.',
    row: 'Row {row}: {errors}',
    rowErrors: '...and {count} more errors',
    duplicateSkuInRows: 'Duplicate SKU "{sku}" found in rows: {rows}',
    quantityExceedsMax: 'Row {row}: Quantity {quantity} exceeds maximum allowed ({max})',
    quantityBelowMin: 'Row {row}: Quantity {quantity} is below minimum required ({min})',
    // Validation messages
    unsupportedFileFormat: 'Unsupported file format. Please upload a CSV or Excel file.',
    csvParsingErrors: 'CSV parsing errors occurred',
    failedToParseFile: 'Failed to parse file',
    failedToParseExcel: 'Failed to parse Excel file',
    failedToReadFile: 'Failed to read file',
    unknownError: 'Unknown error occurred',
    totalRows: 'Total rows',
    valid: 'Valid',
    invalid: 'Invalid',
    skipped: 'Skipped',
    // SKU validation
    skuValidationError: 'SKU Validation Error',
    skuValidationFailed: 'Failed to validate SKUs. You can still proceed but some items may not be available.',
    skuNotFound: 'SKU not found in database',
    skusNotFound: '{count} SKUs not found in database',
    itemsHaveInsufficientStock: '{count} items have insufficient stock',
    validatingSKUs: 'Validating items...',
    unvalidatedChanges: 'You have unvalidated changes. Please revalidate before adding to cart.',
    revalidate: 'Revalidate',
    validating: 'Validating...',
    validationSuccess: 'All items are valid',
    validationComplete: 'Validation Complete',
    validationResultSummary: '{errors} errors, {warnings} warnings found',
    // Table headers
    actions: 'Actions',
    notes: 'Notes',
    // Status filter
    all: 'All',
    hasErrors: 'Has Errors',
    // Selection summary
    selected: 'selected',
    errors: 'errors',
    // Pagination
    page: 'Page {current} of {total}',
    previous: 'Previous',
    next: 'Next',
    noResults: 'No results.',
    // Cart integration
    noValidItems: 'No Valid Items',
    allItemsHaveErrors: 'All items have validation errors. Please fix them before proceeding.',
    dataLoadingError: 'Data Loading Error',
    failedToLoadProductData: 'Failed to load product data. Please try again.',
    itemsAddedToCart: '{count} Items Added to Cart',
    itemsAddedMessage: 'Added {count} items to cart',
    someSkusNotFound: '{count} SKUs could not be found.',
    noItemsAdded: 'No Items Added',
    couldNotFindProducts: 'Could not find any products matching the SKUs.',
    errorAddingToCart: 'Error',
    failedToAddToCart: 'Failed to add items to cart. Please try again.',
    addingToCart: 'Adding to cart...',
    // File info
    fileSelected: '{name} selected for processing',
    fileSize: '{size} bytes',
    fileSizeKB: '{size} KB',
    fileSizeMB: '{size} MB',
    // Template download
    templateDownload: 'Template Download',
    templateWillBeImplemented: 'Template download will be implemented in Phase 7',
    // Additional validation
    validationFailed: 'Validation failed',
    // Template download
    templateDownloaded: 'Template downloaded successfully',
    templateDownloadError: 'Failed to download template',
    // Template content
    sheetName: 'Bulk Order',
    instructionsSheetName: 'Instructions',
    columnDescriptions: 'Column Descriptions:',
    columnHeader: 'Column',
    columnRequired: 'Required',
    columnOptional: 'Optional',
    columnDescription: 'Description',
    templateNotes: 'Notes:',
    templateNote1: 'The first row must contain column headers exactly as shown',
    templateNote2: 'Data starts from row 2',
    templateNote3: 'SKU must match existing product codes in the system',
    templateNote4: 'Quantity must be a positive integer between 1 and 9999',
    templateNote5: 'Maximum 1000 rows allowed per file',
    templateNote6: 'Save as .xlsx or .csv format',
    stockAvailabilityWarning: 'Actual stock availability will be checked on the next checkout page.',
    outOfStock: 'Out of stock',
    insufficientStock: 'Insufficient stock: {available} available, {requested} requested',
  },
  orderExport: {
    export: 'Export Order',
    exportOrder: 'Export Order Data',
    exportAsCSV: 'Export as CSV',
    exportAsExcel: 'Export as Excel',
    exportForReorder: 'Export for Bulk Import',
    detailed: 'Full Details',
    simpleFormat: 'Import Format',
  },
};